import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchSelect } from "@/components/SearchSelect";
import { Plus, Trash2 } from "lucide-react";
import { PriceSchedule } from "@/components/PriceSchedule";
import BundleSummary from "@/components/b2b/BundleSummary";
import { useI18n } from "@/i18n";

// Form dialog for fixed Bundles. State lives in the parent page; this is a controlled view.
export default function BundleFormDialog({
  open, onOpenChange, editId, f, setF, setPrice, setTax, taxRates,
  addComp, updComp, rmComp, optionsFor, dimStr, availMap, save, load,
  sellable, compCostNet, netNum, grossNum, ertragNum,
}) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl [&>*]:min-w-0">
        <DialogHeader><DialogTitle>{editId ? t("bundle.edit") : t("bundle.new")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 min-w-0 [&>div]:min-w-0">
          <div><Label>SKU *</Label><Input data-testid="bundle-sku" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} className="mt-1.5" /></div>
          <div><Label>{t("common.name")} *</Label><Input data-testid="bundle-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>{t("common.priceNet")} *</Label><Input type="number" step="0.01" value={f.price_net} onChange={(e) => setPrice("price_net", e.target.value)} className="mt-1.5" data-testid="bundle-price-net" /></div>
          <div><Label>{t("common.taxRate")}</Label>
            <Select value={String(f.tax_rate)} onValueChange={(v) => setTax(v)}>
              <SelectTrigger className="mt-1.5" data-testid="bundle-tax"><SelectValue /></SelectTrigger>
              <SelectContent>{taxRates.map((r) => <SelectItem key={r} value={String(r)}>{r}%{r === 0 ? ` (${t("common.exportRate")})` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>{t("common.priceGross")} *</Label><Input type="number" step="0.01" value={f.price_gross} onChange={(e) => setPrice("price_gross", e.target.value)} className="mt-1.5" data-testid="bundle-price-gross" /><p className="text-xs text-muted-foreground mt-1">{t("bundle.priceHint")}</p></div>
          <label className="col-span-2 flex items-center gap-2 rounded-md bg-muted/40 p-2 cursor-pointer">
            <Checkbox checked={!!f.strap_option} onCheckedChange={(v) => setF({ ...f, strap_option: !!v })} data-testid="bundle-strap-option" />
            <span className="text-sm">{t("prod.strapOption")}</span>
          </label>
          {editId && <PriceSchedule entityType="bundle" entityId={editId} taxRate={+f.tax_rate || 19} taxRates={taxRates} onApplied={load} />}
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <Label>{t("bundle.components")}</Label>
            <Button size="sm" variant="outline" onClick={addComp} data-testid="add-component-btn"><Plus className="h-3.5 w-3.5 mr-1" /> {t("bundle.component")}</Button>
          </div>
          <div className="space-y-2">
            {f.components.map((c, i) => (
              <div key={c._k || i} className="flex items-center gap-2 min-w-0">
                <div className="flex-1 min-w-0">
                <SearchSelect
                  testId={`comp-variant-${i}`}
                  value={c.component_variant_id}
                  onChange={(v) => updComp(i, "component_variant_id", v)}
                  placeholder={t("bundle.chooseProduct")}
                  className={!c.component_variant_id ? "border-destructive" : ""}
                  options={optionsFor(i).map((v) => { const d = dimStr(v); return { value: v.id, label: `${v.product_name} · ${v.sku}${d ? ` · ${d}` : ""} · ${v.is_digital ? t("common.digital") : `${t("inv.available")}: ${availMap[v.id] ?? 0}`}` }; })}
                />
                </div>
                <Input type="number" min="1" step="1" className="w-20" value={c.quantity} onChange={(e) => updComp(i, "quantity", Math.max(1, Math.floor(Number(e.target.value) || 1)))} data-testid={`comp-qty-${i}`} />
                <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                  <Checkbox checked={c.is_free_component} onCheckedChange={(v) => updComp(i, "is_free_component", !!v)} /> {t("bundle.free")}
                </label>
                <Button size="icon" variant="ghost" onClick={() => rmComp(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
            {f.components.length === 0 && <p className="text-xs text-muted-foreground">{t("bundle.noComponents")}</p>}
          </div>
          <BundleSummary prefix="bundle" showSellable={f.components.length > 0} sellable={sellable} compCostNet={compCostNet} netNum={netNum} grossNum={grossNum} ertragNum={ertragNum} />
        </div>
        <p className="text-xs text-muted-foreground mt-2">{t("bundle.onceHint")}</p>
        <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 mt-1 border-t border-border bg-background px-6 py-3"><Button data-testid="save-bundle-btn" onClick={save} disabled={!f.sku || !f.name || f.components.length === 0 || f.components.some((c) => !c.component_variant_id)}>{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
